import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, GripHorizontal, Minus } from 'lucide-react';

/**
 * FloatingPanel — a fixed-position chat-bubble style overlay that
 * hovers above the current page.  Used for Chat & Calls and Calendar
 * (and any future floating tools).
 *
 * Capabilities:
 *   • Renders into document.body via a portal so it's never clipped.
 *   • Click outside / press Escape → auto-minimises (calls onClose).
 *   • Drag the title-bar (mouse OR touch) to reposition anywhere on
 *     screen.  Position clamps to keep at least 32px of the panel
 *     in-viewport so it can always be grabbed back.
 *   • Resize from any side or corner via a native CSS `resize`
 *     handle in the bottom-right (`overflow: auto` wrapper).  Min &
 *     max sizes are bounded.
 *   • Position + size persisted to localStorage per-`testId`.
 *   • All interactive controls inside the panel (typing, scrolling,
 *     clicking buttons) are unaffected — the drag listener is only
 *     attached to the header strip.
 */
// Versioned key — bumping the version forces a clean slate when the
// component's default sizing logic changes (so users don't get
// stuck with stale 320×360 boxes from an earlier build).
const PANEL_STATE_KEY_PREFIX = 'bbc:floating-panel:v2:';

function loadPersisted(key) {
  try {
    const raw = localStorage.getItem(PANEL_STATE_KEY_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function savePersisted(key, state) {
  try { localStorage.setItem(PANEL_STATE_KEY_PREFIX + key, JSON.stringify(state)); } catch { /* quota */ }
}

export default function FloatingPanel({
  open,
  onClose,
  title,
  children,
  // Initial size defaults — overridden by persisted state if available.
  defaultWidth = 640,
  defaultHeight = 640,
  minWidth = 320,
  minHeight = 360,
  testId,
  icon = null,
  badge = 0,
}) {
  const panelRef = useRef(null);
  const persistKey = testId || 'unnamed';

  // Minimise → floating balloon. Children stay MOUNTED (just hidden) so the
  // open conversation, scroll position AND the typed-but-unsent draft are all
  // preserved when the user collapses the panel.
  const [minimized, setMinimized] = useState(false);
  // Reopening the panel from the header always shows the full view.
  useEffect(() => { if (open) setMinimized(false); }, [open]);

  // ── position & size state, hydrated from localStorage ────────────
  const [box, setBox] = useState(() => {
    const persisted = loadPersisted(persistKey);
    const w = persisted?.w || defaultWidth;
    const h = persisted?.h || defaultHeight;
    // Default top-right under the header strip
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const x = persisted?.x ?? Math.max(8, vw - w - 16);
    const y = persisted?.y ?? 72;
    return { x, y, w, h };
  });

  // Persist when box changes (debounced via micro-batching on next idle)
  useEffect(() => {
    const t = setTimeout(() => savePersisted(persistKey, box), 250);
    return () => clearTimeout(t);
  }, [box, persistKey]);

  // Keep the panel fully inside the *visible* viewport on resize / open.
  // We use visualViewport when available so Android system bars + the
  // browser/TWA chrome are excluded (otherwise 100vh over-reports the
  // height and the panel's bottom — incl. its scroll area — falls off
  // screen and becomes unreachable). Crucially we also account for the
  // panel's top offset (y) so y + height never exceeds the viewport.
  useEffect(() => {
    if (!open) return;
    const clamp = () => {
      const vv = window.visualViewport;
      const vw = vv?.width || window.innerWidth;
      const vh = vv?.height || window.innerHeight;
      setBox((b) => {
        const w = Math.max(minWidth, Math.min(b.w, vw - 8));
        const h = Math.max(minHeight, Math.min(b.h, vh - 8));
        // Pin top/left so the whole panel stays on screen.
        const x = Math.max(0, Math.min(b.x, vw - w - 4));
        const y = Math.max(0, Math.min(b.y, vh - h - 4));
        return { x, y, w, h };
      });
    };
    window.addEventListener('resize', clamp);
    window.visualViewport?.addEventListener('resize', clamp);
    window.visualViewport?.addEventListener('scroll', clamp);
    clamp();
    return () => {
      window.removeEventListener('resize', clamp);
      window.visualViewport?.removeEventListener('resize', clamp);
      window.visualViewport?.removeEventListener('scroll', clamp);
    };
  }, [open, minWidth, minHeight]);

  // ── Balloon (minimised bubble) position — draggable + persisted ──
  const [balloonPos, setBalloonPos] = useState(() => {
    try {
      const raw = localStorage.getItem(`bbc:balloonPos:${persistKey}`);
      const p = raw ? JSON.parse(raw) : null;
      if (p && typeof p.x === 'number' && typeof p.y === 'number') return p;
    } catch { /* ignore */ }
    return null; // null → default bottom-right
  });
  const balloonDrag = useRef({ active: false, moved: false, offX: 0, offY: 0, w: 56, h: 48 });

  // TWA-safe drag: window move/up listeners are attached ONLY while a
  // drag is actively in progress (between down and up), then removed.
  // Keeping a permanent global `touchmove` listener (even a no-op one)
  // makes the Android WebView swallow native gestures — breaking file
  // pickers, downloads and link taps.  So we never leave one mounted.
  const onBalloonDown = useCallback((e) => {
    const evt = e.touches ? e.touches[0] : e;
    const el = e.currentTarget.getBoundingClientRect();
    balloonDrag.current = {
      active: true,
      moved: false,
      offX: evt.clientX - el.left,
      offY: evt.clientY - el.top,
      w: el.width,
      h: el.height,
    };
    const onMove = (ev) => {
      const d = balloonDrag.current;
      if (!d.active) return;
      d.moved = true;
      const m = ev.touches ? ev.touches[0] : ev;
      const vw = window.innerWidth, vh = window.innerHeight;
      const x = Math.max(4, Math.min(m.clientX - d.offX, vw - d.w - 4));
      const y = Math.max(4, Math.min(m.clientY - d.offY, vh - d.h - 4));
      setBalloonPos({ x, y });
      if (ev.cancelable && ev.type === 'touchmove') ev.preventDefault();
    };
    const onUp = () => {
      const d = balloonDrag.current;
      d.active = false;
      if (d.moved) {
        setBalloonPos((p) => {
          try { localStorage.setItem(`bbc:balloonPos:${persistKey}`, JSON.stringify(p)); } catch { /* quota */ }
          return p;
        });
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [persistKey]);

  // ── ESC to minimise (preserves the draft) ───────────────────────
  useEffect(() => {
    if (!open || minimized) return;
    const onKey = (e) => { if (e.key === 'Escape') setMinimized(true); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, minimized]);

  // ── Click-outside to minimise (keeps the panel + draft alive) ────
  // Uses BUBBLE phase (not capture) so the original tap target — e.g. an
  // upload button or a link — handles its own gesture FIRST. A capture-phase
  // document touchstart listener pre-empts the gesture and, in the Android
  // TWA WebView, breaks the trusted-gesture chain needed to open native file
  // pickers / downloads. Bubble phase still lets us minimise on outside taps.
  useEffect(() => {
    if (!open || minimized) return;
    const onDocPointer = (e) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target)) return;
      if (e.target.closest('[data-floating-trigger]')) return;
      if (e.target.closest(`[data-testid="${persistKey}-balloon"]`)) return;
      // Avoid closing while user is actively dragging from inside
      if (panelRef.current.dataset.dragging === '1') return;
      setMinimized(true);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
    };
  }, [open, minimized, persistKey]);

  // ── Drag handlers (mouse + touch) ────────────────────────────────
  const dragRef = useRef({ active: false, offX: 0, offY: 0 });
  const onDragStart = useCallback((e) => {
    const evt = e.touches ? e.touches[0] : e;
    dragRef.current.active = true;
    dragRef.current.offX = evt.clientX - box.x;
    dragRef.current.offY = evt.clientY - box.y;
    if (panelRef.current) panelRef.current.dataset.dragging = '1';
    e.preventDefault();
    const onMove = (ev) => {
      if (!dragRef.current.active) return;
      const m = ev.touches ? ev.touches[0] : ev;
      const vw = window.innerWidth, vh = window.innerHeight;
      const x = Math.max(-box.w + 64, Math.min(m.clientX - dragRef.current.offX, vw - 32));
      const y = Math.max(0, Math.min(m.clientY - dragRef.current.offY, vh - 32));
      setBox((b) => ({ ...b, x, y }));
      if (ev.cancelable && ev.type === 'touchmove') ev.preventDefault();
    };
    const onUp = () => {
      dragRef.current.active = false;
      if (panelRef.current) panelRef.current.dataset.dragging = '0';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [box.x, box.y, box.w]);

  // ── Resize handlers (custom grip in bottom-right) ────────────────
  // Drag the visible diagonal-stripes handle to resize.  We compute
  // the new width/height as the pointer position minus the panel's
  // top-left, clamped to [min, viewport].  Works on mouse + touch.
  const resizeRef = useRef({ active: false, startW: 0, startH: 0, startX: 0, startY: 0 });
  const onResizeStart = useCallback((e) => {
    const evt = e.touches ? e.touches[0] : e;
    resizeRef.current = {
      active: true,
      startW: box.w,
      startH: box.h,
      startX: evt.clientX,
      startY: evt.clientY,
    };
    if (panelRef.current) panelRef.current.dataset.dragging = '1';
    e.preventDefault();
    e.stopPropagation();
    const onMove = (ev) => {
      if (!resizeRef.current.active) return;
      const m = ev.touches ? ev.touches[0] : ev;
      const vw = window.innerWidth, vh = window.innerHeight;
      const dx = m.clientX - resizeRef.current.startX;
      const dy = m.clientY - resizeRef.current.startY;
      const w = Math.max(minWidth, Math.min(resizeRef.current.startW + dx, vw - box.x - 8));
      const h = Math.max(minHeight, Math.min(resizeRef.current.startH + dy, vh - box.y - 8));
      setBox((b) => ({ ...b, w, h }));
      if (ev.cancelable && ev.type === 'touchmove') ev.preventDefault();
    };
    const onUp = () => {
      resizeRef.current.active = false;
      if (panelRef.current) panelRef.current.dataset.dragging = '0';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [box.w, box.h, box.x, box.y, minWidth, minHeight]);

  if (!open) return null;

  return createPortal(
    <>
      {/* Minimised balloon — stays visible across page navigation (it lives in
          the layout). Tap to restore the full panel with the draft intact. */}
      {minimized && (
        <button
          type="button"
          onClick={() => { if (!balloonDrag.current.moved) setMinimized(false); }}
          onMouseDown={onBalloonDown}
          onTouchStart={onBalloonDown}
          data-testid={`${persistKey}-balloon`}
          aria-label={`Open ${title}`}
          style={balloonPos ? { left: balloonPos.x, top: balloonPos.y, right: 'auto', bottom: 'auto' } : undefined}
          className="fixed bottom-5 right-5 z-[62] flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-forest-700 text-white shadow-2xl hover:bg-forest-800 active:scale-95 transition-colors cursor-grab active:cursor-grabbing touch-none select-none animate-in fade-in zoom-in-95"
        >
          <span className="relative flex items-center justify-center">
            {icon || <GripHorizontal className="w-5 h-5" />}
            {badge > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </span>
          <span className="text-sm font-medium max-w-[120px] truncate">{title}</span>
        </button>
      )}

      <div
        ref={panelRef}
        data-testid={testId}
        data-dragging="0"
        style={{
          left: box.x,
          top: box.y,
          width: box.w,
          height: box.h,
          minWidth,
          minHeight,
          maxWidth: 'calc(100dvw - 8px)',
          maxHeight: 'calc(100dvh - 8px)',
          display: minimized ? 'none' : 'flex',
        }}
        className="fixed z-[60] flex-col rounded-2xl bg-white shadow-2xl border border-border animate-in fade-in zoom-in-95 duration-150 overflow-hidden"
      >
        <div
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          className="flex items-center justify-between px-3 py-2 bg-forest-50 border-b border-forest-100 cursor-grab active:cursor-grabbing select-none touch-none"
          data-testid={`${persistKey}-drag-handle`}
        >
          <div className="flex items-center gap-1.5 text-forest-900">
            <GripHorizontal className="w-4 h-4 opacity-40" />
            <h3 className="font-medium text-sm">{title}</h3>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => setMinimized(true)}
              className="p-1 rounded-full hover:bg-forest-100 transition-colors"
              aria-label="Minimise"
              data-testid={`${persistKey}-minimise-btn`}
            >
              <Minus className="w-4 h-4 text-forest-700" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-full hover:bg-forest-100 transition-colors"
              aria-label="Close"
              data-testid={`${persistKey}-close-btn`}
            >
              <X className="w-4 h-4 text-forest-700" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto bg-background">
          {children}
        </div>
        {/* Custom resize handle (visible diagonal stripes, bottom-right). */}
        <div
          onMouseDown={onResizeStart}
          onTouchStart={onResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          data-testid={`${persistKey}-resize-handle`}
          className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize select-none touch-none"
          style={{
            backgroundImage:
              'linear-gradient(135deg, transparent 0%, transparent 40%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.35) 50%, transparent 50%, transparent 70%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.35) 80%, transparent 80%)',
          }}
        />
      </div>
    </>,
    document.body,
  );
}
